import type { SagaReturnType } from 'redux-saga/effects';
import { all, delay, put, select, take, takeLatest } from 'redux-saga/effects';

import { DTOError } from 'src/error/errors/DTOError';
import { logErrorToSentry } from 'src/error/utils/logErrorToSentry';
import { ERROR_TYPE } from 'src/store/error/constants';
import {
  sendPayAnalytics,
  sendPaymentSuccessWL,
} from 'src/store/paymentAnalytics/sagas';

import { RequestError } from '@lt/api';
import type { PayCardSuccess } from '@lt/api/dist/methods/payment/types';
import type { OverloadingReturnType } from 'src/types/utility';
// Если бы у нас было FSD, то оплаты были бы страницей, а создание заказа фичой, так что не вижу проблем :)
import {
  createOrderAction,
  createOrderFail,
  prebuildOrderFail,
  prebuildOrderSuccess,
  submitPackageAction,
} from 'src/routes/Checkout/store/actions/order';
import { getIsPriceChangedOnPay } from 'src/routes/Checkout/store/selectors/order';
import type { Action } from 'redux-saga';
import type { QRMethod } from 'src/routes/Payment/store/qrPayment/types';
import type {
  YandexPayRequest,
  YandexSplitRequest,
} from '@lt/api/dist/methods/payment/methods/paymentParams';
import { getBrowserInfo, PAY_ORDER, PAYMENT_ERRORS } from './constants';

import { api } from '../../api';

import {
  getCardData,
  getCurrentTariffPrepayment,
  getPaymentAmount,
  getPaymentMethod,
  selectOrderID,
  selectOrderPin,
  selectPaymentId,
  selectProcessorIdentifier,
  selectTbankCreditCode,
  selectCurrentVantaTariffID,
  selectYandexAdditionalData,
} from './selectors';

import { setError } from '../error/slice';
import type { NewErrorPayload } from '../error/types';
import { resetPopupState, setPopupState } from '../popup/slice';
import { searchTypeSelector } from '../view/selectors';
import {
  confirmPayment,
  initPayment,
  paymentFailed,
  payUniteller,
  payWithYandex,
  submitCres,
  submitPares,
} from './actions';
import {
  finishPayment,
  openPay2meFrame,
  payCardSuccessed,
  set3dsData,
} from './slice';

function* createPayment() {
  try {
    yield* sendPayAnalytics('payment_started');
    const paymentMethod: SagaReturnType<typeof getPaymentMethod> = yield select(
      getPaymentMethod,
    );
    if (!paymentMethod) {
      throw new Error(
        `initPayment ОПЛАТА НЕ ПРОЙДЕТ, потому что paymentMethod ${paymentMethod}`,
      );
    }

    const orderId: SagaReturnType<typeof selectOrderID> = yield select(
      selectOrderID,
    );
    if (!orderId) throw new Error(`Заказ еще не создан, orderId: ${orderId}`);

    switch (paymentMethod) {
      case 'sbp':
        yield* processSbpPayment(orderId);
        break;
      case 't_pay':
        yield* processQRPayment(orderId, paymentMethod);
        break;
      case 'alfa_pay':
        yield* processQRPayment(orderId, paymentMethod);
        break;
      case 'yandex_pay':
      case 'yandex_split':
        break;
      case 'paylate':
      case 'paylate_120':
      case 'bnpl_paylate':
        yield* processPaylate(orderId, paymentMethod);
        break;
      case 't_bank_credit':
      case 't_bank_credit_flex':
        yield* processTbankCredit(orderId, paymentMethod);
        break;
      case 'vanta':
        yield* processVanta(orderId, paymentMethod);
        break;
      case 'pay_later':
      case 'cash':
        yield put(finishPayment());
        break;
      default:
        yield* processCardPayment();
        break;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error, PAYMENT_ERRORS.PAYMENT_CREATION);
    yield* handlePaymentFailed({
      type: ERROR_TYPE.ORDER,
      action: PAY_ORDER,
      message: PAYMENT_ERRORS.PAYMENT_CREATION,
      error: error instanceof Error ? error : undefined,
    });
  }
}

function* processTbankCredit(
  orderID: number,
  method: 't_bank_credit' | 't_bank_credit_flex',
) {
  try {
    const isFlex = method === 't_bank_credit_flex';
    const pin: SagaReturnType<typeof selectOrderPin> = yield select(
      selectOrderPin,
    );
    const tariffID: SagaReturnType<typeof selectTbankCreditCode> = yield select(
      selectTbankCreditCode,
      isFlex,
    );

    window.location.href = `${window.location.origin}/payment/t_bank_credit?tariffID=${tariffID}&orderID=${orderID}&pin=${pin}&flex=${isFlex}`;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error, PAYMENT_ERRORS.TBANK_CREDIT);
    yield* handlePaymentFailed({
      type: ERROR_TYPE.ORDER,
      action: PAY_ORDER,
      message:
        error instanceof RequestError
          ? error.message
          : PAYMENT_ERRORS.TBANK_CREDIT,
      error: error instanceof DTOError ? error : undefined,
    });
  }
}

function* processVanta(orderID: number, method: 'vanta') {
  try {
    const currentVantaTariffID: SagaReturnType<
      typeof selectCurrentVantaTariffID
    > = yield select(selectCurrentVantaTariffID);
    if (!currentVantaTariffID)
      throw new Error('У Ванты не выбран тарифный план');
    const params = {
      payment_method: method,
      order_id: orderID,
      plan_type: currentVantaTariffID,
    } as const;
    const response: OverloadingReturnType<
      typeof api.postPaymentsPaymentParams,
      typeof params
    > = yield api.postPaymentsPaymentParams(params);
    window.location.href = response.redirect;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error, PAYMENT_ERRORS.VANTA);
    yield* handlePaymentFailed({
      type: ERROR_TYPE.ORDER,
      action: PAY_ORDER,
      message: error instanceof Error ? error.message : PAYMENT_ERRORS.VANTA,
      error: error instanceof Error ? error : undefined,
    });
  }
}

function* processQRPayment(orderId: number, method: 'alfa_pay' | 't_pay') {
  const pin: SagaReturnType<typeof selectOrderPin> = yield select(
    selectOrderPin,
  );
  const searchType: SagaReturnType<typeof searchTypeSelector> = yield select(
    searchTypeSelector,
  );
  window.location.href = `${window.location.origin}/payment/${method}?&order_id=${orderId}&pin=${pin}&search_type=${searchType}`;
}

function* waitForOrderCreation(action: ReturnType<typeof payWithYandex>) {
  const { method, resolve, reject } = action.payload;
  try {
    const yandexData: SagaReturnType<typeof selectYandexAdditionalData> =
      yield select(selectYandexAdditionalData);
    if (!yandexData) return;

    yield put(submitPackageAction());

    yield take([prebuildOrderSuccess, prebuildOrderFail, createOrderAction]);

    const isPriceChanged: SagaReturnType<typeof getIsPriceChangedOnPay> =
      yield select(getIsPriceChangedOnPay);
    if (isPriceChanged) {
      reject(new Error('Price has been changed'));
      return;
    }

    const act: Action = yield take([initPayment, createOrderFail]);
    if (act.type === createOrderFail.type) {
      reject(new Error('Order creation fail'));
      return;
    }

    const orderId: SagaReturnType<typeof selectOrderID> = yield select(
      selectOrderID,
    );
    if (!orderId) throw new Error(`Заказ еще не создан, orderId: ${orderId}`);

    if (method === 'yandex_pay') {
      const params: YandexPayRequest = {
        payment_method: 'yandex_pay',
        order_id: orderId,
      };

      const response: OverloadingReturnType<
        typeof api.postPaymentsPaymentParams,
        typeof params
      > = yield api.postPaymentsPaymentParams(params);

      resolve(response.data.url);
    }

    if (method === 'yandex_split') {
      const params: YandexSplitRequest = {
        payment_method: 'yandex_split',
        order_id: orderId,
      };

      const response: OverloadingReturnType<
        typeof api.postPaymentsPaymentParams,
        typeof params
      > = yield api.postPaymentsPaymentParams(params);

      resolve(response.data.url);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    const paymentError =
      method === 'yandex_pay'
        ? PAYMENT_ERRORS.YANDEX_PAY
        : PAYMENT_ERRORS.YANDEX_SPLIT;

    console.error(error, paymentError);
    yield* handlePaymentFailed({
      type: ERROR_TYPE.ORDER,
      action: PAY_ORDER,
      message: error instanceof RequestError ? error.message : paymentError,
      error: error instanceof DTOError ? error : undefined,
    });
    reject(error);
  }
}

function* processSbpPayment(orderId: number) {
  try {
    const params = {
      payment_method: 'sbp',
      order_id: orderId,
    } as const;
    const response: OverloadingReturnType<
      typeof api.postPaymentsPaymentParams,
      typeof params
    > = yield api.postPaymentsPaymentParams(params);
    const { paymentId } = response;

    const pin: SagaReturnType<typeof selectOrderPin> = yield select(
      selectOrderPin,
    );
    const searchType: SagaReturnType<typeof searchTypeSelector> = yield select(
      searchTypeSelector,
    );
    window.location.href = `${window.location.origin}/payment/sbp?payment_id=${paymentId}&order_id=${orderId}&pin=${pin}&search_type=${searchType}`;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error, PAYMENT_ERRORS.SBP);
    yield* handlePaymentFailed({
      type: ERROR_TYPE.ORDER,
      action: PAY_ORDER,
      message:
        error instanceof RequestError ? error.message : PAYMENT_ERRORS.SBP,
      error: error instanceof DTOError ? error : undefined,
    });
  }
}

function* processPaylate(
  orderId: number,
  paylateTariff: 'paylate' | 'paylate_120' | 'bnpl_paylate',
) {
  try {
    const params = {
      payment_method: paylateTariff,
      order_id: orderId,
    } as const;

    const response: OverloadingReturnType<
      typeof api.postPaymentsPaymentParams,
      typeof params
    > = yield api.postPaymentsPaymentParams(params);
    window.location.href = response.redirect;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error, PAYMENT_ERRORS.PAYLATE);
    yield* handlePaymentFailed({
      type: ERROR_TYPE.ORDER,
      action: PAY_ORDER,
      message:
        error instanceof RequestError ? error.message : PAYMENT_ERRORS.PAYLATE,
      error: error instanceof DTOError ? error : undefined,
    });
  }
}

/**
 * Обработка платежа uniteller
 */
function* processCardPayment() {
  try {
    const orderId: SagaReturnType<typeof selectOrderID> = yield select(
      selectOrderID,
    );
    if (!orderId) throw new Error('Order is not yet created');

    const cardData: SagaReturnType<typeof getCardData> = yield select(
      getCardData,
    );
    const amount: SagaReturnType<typeof getPaymentAmount> = yield select(
      getPaymentAmount,
    );
    const paymentMethod: SagaReturnType<typeof getPaymentMethod> = yield select(
      getPaymentMethod,
    );
    const currentTariffPrepayment: SagaReturnType<
      typeof getCurrentTariffPrepayment
    > = yield select(getCurrentTariffPrepayment);

    if (!cardData) {
      throw new Error(`Не пришли данные cardData: ${cardData}`);
    }
    const { number: pan, expMonth, expYear, cvv } = cardData;

    if (!amount) {
      throw new Error(
        `handleUnitellerPayment ОПЛАТА НЕ ПРОЙДЕТ, потому что amount: ${amount}`,
      );
    }

    if (paymentMethod === 'tariff_prepayment' && !currentTariffPrepayment) {
      throw new Error(
        `processCardPayment ОПЛАТА НЕ ПРОЙДЕТ, потому что paymentMethod: ${paymentMethod} и currentTariffPrepayment: ${currentTariffPrepayment}`,
      );
    }

    const requestValues = {
      pan,
      cvv,
      exp_month: expMonth,
      exp_year: `20${expYear}`,
      amount,
      payment_method: paymentMethod,
      order_id: orderId,
      browser_info: getBrowserInfo(),
    };

    const response: SagaReturnType<typeof api.postPaymentsPayCard> =
      yield api.postPaymentsPayCard(
        paymentMethod === 'tariff_prepayment' && currentTariffPrepayment
          ? {
              ...requestValues,
              tariff_prepayment_type: currentTariffPrepayment.type,
              amount: currentTariffPrepayment.amount,
            }
          : requestValues,
      );

    const { paymentId } = response;

    if ('required3DS' in response) {
      yield put(finishPayment());
      return;
    }

    yield put(
      payCardSuccessed({
        paymentId,
        processorIdentifier: response.processorIdentifier,
      }),
    );

    switch (response.processorIdentifier) {
      case 'uniteller_v1':
        yield put(
          set3dsData({
            processorIdentifier: response.processorIdentifier,
            pareq: response.pareq,
            md: response.md,
            termURL: response.termURL,
            url: response.url,
          }),
        );
        yield put(setPopupState('3ds'));
        return;
      case 'uniteller_v2':
        yield put(
          set3dsData({
            processorIdentifier: response.processorIdentifier,
            ...('data' in response
              ? { data: response.data, url: response.url }
              : { iframeInlineHTML: response.iframeInlineHTML }),
          }),
        );
        yield put(setPopupState('3ds2.0'));
        return;
      case 'pay2me_v1':
      case 'pay2me_v2':
        yield put(openPay2meFrame(response.url));
        yield put(setPopupState('pay2me'));
        return;
      case 't_bank_v1':
      case 't_bank_v2':
        yield* processTbankAcquiring(response);
        return;
      default:
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
    yield* handlePaymentFailed({
      type: ERROR_TYPE.ORDER,
      action: PAY_ORDER,
      message:
        error instanceof RequestError ? error.message : PAYMENT_ERRORS.PAY_CARD,
      error: error instanceof DTOError ? error : undefined,
    });
  }
}

function* processTbankAcquiring(
  response: Extract<
    PayCardSuccess,
    { processorIdentifier: 't_bank_v1' | 't_bank_v2' }
  >,
) {
  if (!response.url) {
    yield* confirmPayment3ds();
    return;
  }

  yield put(
    set3dsData({
      processorIdentifier: 't_bank_v1',
      url: response.url,
      data: response.data,
    }),
  );
  yield put(setPopupState('3ds2.0'));
}

/**
 * Получаем pares или cres от эквайринга и отдаем на сервер для заморозки платежа
 */
function* handle3dsSubmit(
  action: ReturnType<typeof submitCres> | ReturnType<typeof submitPares>,
) {
  try {
    const { type, payload } = action;

    const paymentId: SagaReturnType<typeof selectPaymentId> = yield select(
      selectPaymentId,
    );

    if (!paymentId) {
      throw new Error(
        `handle3dsPayment ОПЛАТА НЕ ПРОЙДЕТ, потому что payment_id: ${paymentId}`,
      );
    }

    yield api.postPaymentsPay3ds({
      payment_id: paymentId,
      ...(type === 'payment/submitCres'
        ? { cres: payload }
        : { pares: payload }),
    });

    yield put(finishPayment());
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e, PAYMENT_ERRORS.THREE_DS);
    yield* handlePaymentFailed({
      type: ERROR_TYPE.ORDER_3DS,
      action: PAY_ORDER,
      message: e instanceof RequestError ? e.message : PAYMENT_ERRORS.THREE_DS,
      error: e instanceof DTOError ? e : undefined,
    });
  }
}

function* resolveThreeDSPayload() {
  const cardData: SagaReturnType<typeof getCardData> = yield select(
    getCardData,
  );
  const paymentId: SagaReturnType<typeof selectPaymentId> = yield select(
    selectPaymentId,
  );

  if (!paymentId) {
    throw new Error(
      `handleSubmit3dsData ОПЛАТА НЕ ПРОЙДЕТ, потому что payment_id: ${paymentId}`,
    );
  }

  const { number, cvv, expMonth, expYear } = cardData;
  return {
    pan: number,
    cvv,
    exp_month: expMonth,
    exp_year: `20${expYear}`,
    browser_info: getBrowserInfo(),
    payment_id: paymentId,
  };
}

/**
 *  Запрос на подтверждение платежа к банку
 */
function* confirmPayment3ds() {
  try {
    const payload = yield* resolveThreeDSPayload();
    yield put(setPopupState('fetching'));
    const response: SagaReturnType<typeof api.postPaymentsData3ds> =
      yield api.postPaymentsData3ds(payload);
    const { status, data3ds } = response;
    yield put(resetPopupState());
    // Если статус `paid` – значит оплата прошла успешно
    if (status === 'paid') {
      yield put(finishPayment());
      return;
    }

    if (!data3ds) throw new DTOError('No data3ds provided in 3ds2.0', data3ds);

    yield put(set3dsData(data3ds));
    yield put(setPopupState('3ds'));

    const processor: SagaReturnType<typeof selectProcessorIdentifier> =
      yield select(selectProcessorIdentifier);
    if (processor !== 't_bank_v1' && processor !== 't_bank_v2') return;

    yield* statusShortPolling(payload.payment_id, 't_bank_card');
  } catch (error) {
    yield* handlePaymentFailed({
      message:
        error instanceof RequestError
          ? error.message
          : PAYMENT_ERRORS.PAY_ERROR,
      error: error instanceof DTOError ? error : undefined,
    });
  }
}

export function* statusShortPolling(
  paymentId: number,
  paymentMethod: QRMethod | 't_bank_card',
) {
  while (true) {
    const response: OverloadingReturnType<
      typeof api.getPaymentsPaymentStatus,
      { method: typeof paymentMethod }
    > = yield api.getPaymentsPaymentStatus({
      payment_id: paymentId,
      method: paymentMethod,
    });

    if (response.status === 'paid') {
      yield put(finishPayment());
      return;
    }

    yield delay(5000);
  }
}

export function* handlePaymentFailed(
  payload: NewErrorPayload & { error?: Error },
) {
  yield put(setError(payload));
  yield put(setPopupState('error'));

  const { error, techIssue } = payload;
  if (error) {
    logErrorToSentry(error, undefined);
    yield* sendPayAnalytics('payment_failed', techIssue ?? error.message);
  } else {
    logErrorToSentry(new Error(payload.message), undefined);
    yield* sendPayAnalytics('payment_failed', techIssue ?? payload.message);
  }
}

function* callPaymentFailedSaga(action: ReturnType<typeof paymentFailed>) {
  yield* handlePaymentFailed(action.payload);
}

function* callPaymentSuccessSaga() {
  yield* sendPayAnalytics('payment_success');
  yield* sendPaymentSuccessWL();
}

export default function* paymentSaga() {
  yield all([
    // Step 1: INIT
    takeLatest(initPayment, createPayment),
    // Step 2: Uniteller
    takeLatest(payUniteller, processCardPayment),
    // Step 3: 3ds2.0
    takeLatest(confirmPayment, confirmPayment3ds),
    // Step 4: 3ds (optional)
    takeLatest(submitCres, handle3dsSubmit),
    takeLatest(submitPares, handle3dsSubmit),

    takeLatest(paymentFailed, callPaymentFailedSaga),
    takeLatest(finishPayment, callPaymentSuccessSaga),
    takeLatest(payWithYandex, waitForOrderCreation),
  ]);
}
